import { useState, useEffect } from 'react';
import { Rocket, Server, Globe, GitBranch, Clock, CheckCircle, AlertTriangle, X, Loader2, Eye, RotateCcw, Play, Square } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { settingsService } from '../../services/settingsService';
import type { DeploymentData, DeploymentTrigger } from '../../services/settingsService';
import { toast } from 'react-hot-toast';

const DeploymentsTab = () => {
  const [deploymentData, setDeploymentData] = useState<DeploymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadDeploymentData();
  }, []);

  const loadDeploymentData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await settingsService.loadDeploymentData();
      setDeploymentData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load deployment data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const triggerDeployment = async (deploymentId: string, branch?: string, environment?: string) => {
    setActionLoading(prev => ({ ...prev, [deploymentId]: true }));
    try {
      const trigger: DeploymentTrigger = { deploymentId, branch, environment };
      await settingsService.triggerDeployment(trigger);
      toast.success('Deployment triggered successfully');
      await loadDeploymentData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to trigger deployment';
      toast.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [deploymentId]: false }));
    }
  };

  const stopDeployment = async (deploymentId: string) => {
    setActionLoading(prev => ({ ...prev, [`stop_${deploymentId}`]: true }));
    try {
      await settingsService.stopDeployment(deploymentId);
      toast.success('Deployment stopped successfully');
      await loadDeploymentData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to stop deployment';
      toast.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [`stop_${deploymentId}`]: false }));
    }
  };

  const rollbackDeployment = async (deploymentId: string) => {
    setActionLoading(prev => ({ ...prev, [`rollback_${deploymentId}`]: true }));
    try {
      const result = await settingsService.rollbackDeployment(deploymentId);
      toast.success(`Rolled back from ${result.previousVersion} to ${result.newVersion}`);
      await loadDeploymentData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to rollback deployment';
      toast.error(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [`rollback_${deploymentId}`]: false }));
    }
  };

  const viewLogs = async (deploymentId: string) => {
    try {
      const result = await settingsService.getDeploymentLogs(deploymentId);
      console.log('Deployment logs:', result.logs);
      toast.success('Logs retrieved successfully (check console)');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to retrieve logs';
      toast.error(errorMessage);
    }
  };

  const updateSettings = async (setting: string, value: boolean) => {
    try {
      const updates = { [setting]: value };
      await settingsService.updateDeploymentSettings(updates);
      toast.success('Settings updated successfully');
      await loadDeploymentData();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update settings';
      toast.error(errorMessage);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'deploying':
        return <Clock size={16} className="text-yellow-400" />;
      case 'inactive':
        return <X size={16} className="text-slate-400" />;
      default:
        return <AlertTriangle size={16} className="text-red-400" />;
    }
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'production':
        return 'bg-red-400/20 text-red-400';
      case 'staging':
        return 'bg-yellow-400/20 text-yellow-400';
      case 'development':
        return 'bg-green-400/20 text-green-400';
      default:
        return 'bg-slate-400/20 text-slate-400';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Deployments</h2>
          <p className="text-slate-400 mt-1">Monitor and manage your application deployments</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-brand-accent" />
          <span className="ml-3 text-slate-400">Loading deployment data...</span>
        </div>
      </div>
    );
  }

  if (error || !deploymentData) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Deployments</h2>
          <p className="text-slate-400 mt-1">Monitor and manage your application deployments</p>
        </div>
        <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-red-400">{error || 'Failed to load deployment data'}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadDeploymentData}
            className="mt-3"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold violett-gradient-text text-drop-shadow">Deployments</h2>
        <p className="text-slate-400 mt-1">Monitor and manage your application deployments</p>
      </div>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Rocket size={20} className="text-brand-accent" />
              <span>Active Deployments</span>
            </div>
            <Button 
              size="sm"
              onClick={() => triggerDeployment('new', 'main', 'production')}
              disabled={actionLoading['new']}
            >
              {actionLoading['new'] ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <Rocket size={16} className="mr-2" />
              )}
              New Deployment
            </Button>
          </CardTitle>
          <CardDescription>Current deployment status across all environments</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {(deploymentData.deployments || []).map((deployment) => (
              <div key={deployment.id} className="p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg border border-slate-700/40">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-brand-primary/20 rounded-lg">
                      <Server size={20} className="text-brand-accent" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="text-sm font-medium cream-gradient-text text-drop-shadow">{deployment.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEnvironmentColor(deployment.environment)}`}>
                          {deployment.environment}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-400/20 text-blue-400">
                          {deployment.provider}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 mt-1">
                        <div className="flex items-center space-x-1 text-xs text-slate-400">
                          <Globe size={12} />
                          <span>{deployment.url}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-xs text-slate-400">
                          <GitBranch size={12} />
                          <span>{deployment.branch}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(deployment.status)}
                    <span className="text-sm text-slate-300">{deployment.version}</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-slate-400">
                  <span>Last deployed: {new Date(deployment.lastDeploy).toLocaleString()}</span>
                  <div className="flex space-x-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => viewLogs(deployment.id)}
                    >
                      <Eye size={14} className="mr-1" />
                      View Logs
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => rollbackDeployment(deployment.id)}
                      disabled={actionLoading[`rollback_${deployment.id}`]}
                    >
                      {actionLoading[`rollback_${deployment.id}`] ? (
                        <Loader2 size={14} className="mr-1 animate-spin" />
                      ) : (
                        <RotateCcw size={14} className="mr-1" />
                      )}
                      Rollback
                    </Button>
                    {deployment.status === 'active' ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-400 hover:text-red-300"
                        onClick={() => stopDeployment(deployment.id)}
                        disabled={actionLoading[`stop_${deployment.id}`]}
                      >
                        {actionLoading[`stop_${deployment.id}`] ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Square size={14} className="mr-1" />
                        )}
                        Stop
                      </Button>
                    ) : deployment.status === 'inactive' ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-green-400 hover:text-green-300"
                        onClick={() => triggerDeployment(deployment.id)}
                        disabled={actionLoading[deployment.id]}
                      >
                        {actionLoading[deployment.id] ? (
                          <Loader2 size={14} className="mr-1 animate-spin" />
                        ) : (
                          <Play size={14} className="mr-1" />
                        )}
                        Start
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Clock size={20} className="text-brand-accent" />
            <span>Deployment History</span>
          </CardTitle>
          <CardDescription>Recent deployment activity and status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {(deploymentData.history || []).map((deploy) => (
              <div key={deploy.id} className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-brand-primary/20 rounded-lg">
                    {deploy.status === 'success' ? (
                      <CheckCircle size={16} className="text-green-400" />
                    ) : deploy.status === 'deploying' ? (
                      <Clock size={16} className="text-yellow-400" />
                    ) : (
                      <X size={16} className="text-red-400" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-brand-text">{deploy.version}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEnvironmentColor(deploy.environment)}`}>
                        {deploy.environment}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400">{new Date(deploy.timestamp).toLocaleString()}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className={`text-sm font-medium ${
                      deploy.status === 'success' ? 'text-green-400' :
                      deploy.status === 'deploying' ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {deploy.status}
                    </div>
                    <div className="text-xs text-slate-400">{deploy.duration}</div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => viewLogs(deploy.deploymentId)}
                  >
                    <Eye size={14} className="mr-1" />
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/40 backdrop-blur-sm border border-slate-700/30 rounded-xl p-6 hover:border-slate-600/50 transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server size={20} className="text-brand-accent" />
            <span>Deployment Settings</span>
          </CardTitle>
          <CardDescription>Configure deployment preferences and automation</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <GitBranch size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Auto-deploy on merge</div>
                <div className="text-xs text-slate-400">Automatically deploy when code is merged to main</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={deploymentData.settings?.autoDeployOnMerge || false}
                onChange={(e) => updateSettings('autoDeployOnMerge', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <CheckCircle size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Health checks</div>
                <div className="text-xs text-slate-400">Monitor deployment health and auto-rollback on failure</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={deploymentData.settings?.healthChecks || false}
                onChange={(e) => updateSettings('healthChecks', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl hover:shadow-indigo-700/10 transition-all duration-300 rounded-lg">
            <div className="flex items-center space-x-3">
              <AlertTriangle size={20} className="text-brand-accent" />
              <div>
                <div className="text-sm font-medium text-brand-text">Slack notifications</div>
                <div className="text-xs text-slate-400">Send deployment status updates to Slack</div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={deploymentData.settings?.slackNotifications || false}
                onChange={(e) => updateSettings('slackNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-primary"></div>
            </label>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeploymentsTab;
